import React, { useEffect, useState } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface UserRecord {
  id: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: string;
  lastActive: string | null;
  packCount: number;
}

function RoleBadge({ role }: { role: string }) {
  const badgeMap: Record<string, string> = {
    owner: 'warning',
    captain: 'info',
    member: 'muted',
  };
  return (
    <span className={`badge badge--${badgeMap[role] || 'muted'}`}>
      {role}
    </span>
  );
}

export function UsersPage() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formRole, setFormRole] = useState('member');

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await apiFetch<UserRecord[]>('/api/admin/users');
      if (res.success && res.data) {
        setUsers(res.data);
      } else {
        setError(res.error || 'Failed to load users');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setIsCreating(true);
    setEditingUser(null);
    setFormEmail('');
    setFormDisplayName('');
    setFormRole('member');
    setShowModal(true);
  }

  function openEdit(user: UserRecord) {
    setIsCreating(false);
    setEditingUser(user);
    setFormEmail(user.email);
    setFormDisplayName(user.displayName);
    setFormRole(user.role);
    setShowModal(true);
  }

  async function deleteUser(user: UserRecord) {
    if (!confirm(`DELETE user "${user.displayName}" (${user.email})?\n\nThis will remove them from all packs and cannot be undone.`)) return;
    await apiFetch(`/api/admin/users/${user.id}`, { method: 'DELETE' });
    loadUsers();
  }

  async function saveUser() {
    if (isCreating) {
      await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: formEmail,
          display_name: formDisplayName,
          role: formRole,
        }),
      });
    } else if (editingUser) {
      const payload: Record<string, unknown> = {};
      if (formRole !== editingUser.role) payload.role = formRole;
      if (formDisplayName !== editingUser.displayName) payload.display_name = formDisplayName;

      if (Object.keys(payload).length > 0) {
        await apiFetch(`/api/admin/users/${editingUser.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      }
    }

    setShowModal(false);
    loadUsers();
  }

  if (loading) return <div style={styles.loading}>Loading users...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.pageTitle}>Users</h1>
          <p style={styles.pageSubtitle}>User management and role assignment</p>
        </div>
        <button style={styles.btnPrimary} onClick={openCreate}>+ Create User</button>
      </div>

      <div style={styles.card}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Packs</th>
              <th>Joined</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td style={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                  {user.displayName}
                </td>
                <td>{user.email}</td>
                <td><RoleBadge role={user.role} /></td>
                <td>{user.packCount}</td>
                <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '--'}</td>
                <td>{user.lastActive ? new Date(user.lastActive).toLocaleDateString() : '--'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={styles.btnSmallMuted} onClick={() => openEdit(user)}>
                      Edit
                    </button>
                    <button style={styles.btnSmallDanger} onClick={() => deleteUser(user)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.overlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {isCreating ? 'Create User' : `Edit: ${editingUser?.displayName}`}
            </h2>
            {isCreating && (
              <div style={styles.formField}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
              </div>
            )}
            <div style={styles.formField}>
              <label style={styles.label}>Display Name</label>
              <input
                value={formDisplayName}
                onChange={(e) => setFormDisplayName(e.target.value)}
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Role</label>
              <select value={formRole} onChange={(e) => setFormRole(e.target.value)}>
                <option value="member">Member</option>
                <option value="captain">Captain</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={saveUser}>
                {isCreating ? 'Create' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { fontSize: 28, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  pageSubtitle: { fontSize: 14, color: COLORS.textMuted, marginTop: 4 },
  card: {
    backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: 12, padding: 16,
  },
  loading: { display: 'flex', justifyContent: 'center', height: 300, color: COLORS.textMuted },
  error: { padding: 20, backgroundColor: 'rgba(220,53,69,0.1)', borderRadius: 8, color: COLORS.statusError },
  btnPrimary: {
    padding: '8px 16px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold,
    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnSecondary: {
    padding: '8px 16px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary,
    border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  btnSmallMuted: {
    padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.textMuted,
    border: `1px solid ${COLORS.borderDefault}`, borderRadius: 4, fontSize: 12, cursor: 'pointer',
  },
  btnSmallDanger: {
    padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.statusError,
    border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 4, fontSize: 12, cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: COLORS.backgroundOverlay, display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 200,
  },
  modal: {
    width: 420, maxWidth: '90%', backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.borderDefault}`, borderRadius: 16, padding: 32,
  },
  modalTitle: { fontSize: 20, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 24 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 },
  formField: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  label: { fontSize: 12, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
};
